import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import {
  ConfirmationModalComponent,
  DataTableComponent,
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormDropdownComponent,
  FormInputComponent,
  FormInputDateComponent,
  FormInputFileComponent,
  SubDirectionModalComponent,
} from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { ActionsEvents, AdaaBoolean, FileInputType, Language, PageMode } from "../../../shared/constants/enums";
import { SmartToolTipsDirective, SmartToolTipsService } from "../../../shared/directives";
import { AttachFile, SubGovDirection, TableButtonClicked, ValidatorModelType } from "../../../shared/models";
import {
  AppService,
  GovernmentDirectionsEditorService,
  LanguageService,
  SystemLayoutService,
  ValidatorApiService,
} from "../../../shared/services";

@Component({
  selector: "adaa-government-directions-editor",
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormInputComponent,
    FormInputDateComponent,
    FormDropdownComponent,
    FormCheckboxComponent,
    FormInputFileComponent,
    DataTableComponent,
    FormActionButtonsComponent,
    SmartToolTipsDirective,
  ],
  templateUrl: "./government-directions-editor.html",
  styleUrl: "./government-directions-editor.scss",
})
export class GovernmentDirectionsEditorComponent implements OnInit {
  private readonly _languageService = inject(LanguageService);
  private readonly _modalService = inject(NgbModal);
  private readonly _router = inject(Router);
  private readonly _toastrService = inject(ToastrService);
  private readonly _translateService = inject(TranslateService);
  private readonly _systemLayoutService = inject(SystemLayoutService);
  private readonly _appService = inject(AppService);
  private readonly _smartToolTipsService = inject(SmartToolTipsService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _validatorApiService = inject(ValidatorApiService);
  readonly directionsService = inject(GovernmentDirectionsEditorService);

  id = input<string>();
  pageMode = input.required<PageMode>();

  private _untilDestroyed = AdaaHelper.untilDestroyed();
  language = Language;
  readonly Constants = Constants;
  directionForm: FormGroup;
  allowedFiles: FileInputType[] = [FileInputType.svg];
  submitted: boolean = false;
  goToSubmit: boolean = false;
  filteredSubDirections: SubGovDirection[] = [];
  subDirections: SubGovDirection[] = [];
  formValidation = signal<ValidatorModelType | undefined>(undefined);

  isViewMode = computed(() => this.pageMode() === PageMode.view);

  public get directionIcon(): AttachFile {
    return {
      id: this.directionForm.get("logo")?.value,
      filename: this.directionForm.get("logo")?.value ? "logo.svg" : "",
    };
  }

  public get displayLabel(): string {
    const nameEN = this.directionForm.get("nameEN")?.value;
    const nameAE = this.directionForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  public ngOnInit(): void {
    this._prepareForm();
    this.directionsService.initPage();

    const id = this.id();
    if (id) {
      this.directionsService.getDirection(+id).subscribe((direction) => {
        if (!direction) return;
        this.directionForm.patchValue(direction);
        if (direction.isNationalStrategy) this.directionForm.get("nationalStrategyType")?.enable();
        this.subDirections = direction.subdirections ?? [];

        this._setSubDirections();
      });
    }

    this._systemLayoutService.hasCycleChanged$.pipe(this._untilDestroyed()).subscribe({
      next: () => window.location.reload(),
    });

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe({
      next: () => this._router.navigateByUrl(`/console`),
    });

    if (!this.isViewMode())
      this._smartToolTipsService.initSmartToolTips(Constants.SMART_TOOL_TIPS_KEY.SMART_GOVERNMENT_DIRECTION);
  }

  public visionChanged(value: AdaaBoolean) {
    if (value === AdaaBoolean.Y) {
      this.directionForm.get("isNationalStrategy")?.setValue(false);
      this.directionForm.get("isNationalStrategy")?.disable();
    } else {
      this.directionForm.get("isNationalStrategy")?.enable();
    }
  }

  public onFilechange(event: AttachFile | null) {
    this.directionForm.get("logo")?.setValue(event ? event.id : null);
  }

  public addSubDirectionClicked(): void {
    const subDirection: SubGovDirection = {
      govDirectionId: undefined,
      isMain: false,
      refCode: undefined,
      nameAE: "",
      nameEN: "",
      id: 0,
      owner: undefined,
      status: Constants.OBJECT_STATUS.ACTIVE,
    };

    this._openSubDirectionModal(subDirection, PageMode.create);
  }

  public manageSubDirectionClicked({ event, data }: TableButtonClicked): void {
    switch (event) {
      case ActionsEvents.edit:
      case ActionsEvents.view: {
        const subDirection = this.subDirections.find((e) =>
          data.id ? e.id === data.id : data.fakeId ? e.fakeId === data.fakeId : false
        );
        if (subDirection)
          this._openSubDirectionModal(subDirection, event === ActionsEvents.view ? PageMode.view : PageMode.edit);
        break;
      }
      case ActionsEvents.delete: {
        this._deleteSubDirectionConfirmation(data);
        break;
      }
      default:
        break;
    }
  }

  public submit(): void {
    this.submitted = true;
    this.goToSubmit = false;

    if (!this.directionForm.valid) {
      this._toastrService.warning(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    if (!AdaaHelper.isDefined(this.subDirections) || this.subDirections.length === 0) {
      this._openDuplicateDirectionModal();
      return;
    }

    const id = this.id();
    this.directionsService.saveDirection(id ? +id : undefined, this.directionForm.getRawValue());
  }

  public cancel(): void {
    this._router.navigate(["/console/directions"]);
  }

  private _openSubDirectionModal(subDirection: SubGovDirection, pageMode: PageMode) {
    const modal = this._modalService.open(SubDirectionModalComponent, {
      centered: true,
      size: "xl",
      modalDialogClass: `modal-${this._languageService.direction()}`,
    });

    modal.componentInstance.subDirection = subDirection;
    modal.componentInstance.pageMode = pageMode;

    modal.result.then((event) => {
      if (!AdaaHelper.isDefined(event)) {
        this.goToSubmit = false;
        return;
      }
      if (this._checkForDuplicateName(event)) {
        this._toastrService.error(this._translateService.instant("directions.sub_name_exists"));
        return;
      }

      const subDirectionIndex = this.subDirections.findIndex((e) =>
        event.id ? event.id === e.id : event.fakeId ? event.fakeId === e.fakeId : false
      );

      if (subDirectionIndex > -1) {
        this.subDirections.splice(subDirectionIndex, 1, event);
      } else {
        delete event.id;
        event.fakeId = Math.floor(Math.random() * 217 * 1000);
        this.subDirections.push(event);
      }

      this._setSubDirections();

      if (this.goToSubmit) this.submit();
    });
  }

  private _openDuplicateDirectionModal(): void {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: `modal-${this._languageService.direction()}`,
    });

    modal.componentInstance.header = "directions.identical_title";
    modal.componentInstance.title = "directions.identical_text";

    modal.result.then((e) => {
      if (e) this._createDuplicateSubDirection();
    });
  }

  private _createDuplicateSubDirection(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subDirection: any = {
      nameAE: this.directionForm.get("nameAE")?.value,
      nameEN: this.directionForm.get("nameEN")?.value,
      dscAE: this.directionForm.get("dscAE")?.value,
      dscEN: this.directionForm.get("dscEN")?.value,
      status: Constants.OBJECT_STATUS.ACTIVE,
      isMain: false,
      fakeId: Math.floor(Math.random() * 217 * 1000),
      owner: null,
    };

    this.goToSubmit = true;
    this._openSubDirectionModal(subDirection, PageMode.edit);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _deleteSubDirectionConfirmation(data: any): void {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    modal.componentInstance.header = "directions.sub_government";
    modal.componentInstance.title = "directions.subDirectionRemove";

    modal.result.then((e) => {
      if (e) this._deleteSubDirectionConfirmed(data);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _deleteSubDirectionConfirmed(data: any): void {
    if (data.id) {
      const subDirection = this.subDirections.find((e) => e.id === data.id);
      if (subDirection) subDirection.status = Constants.OBJECT_STATUS.REMOVE;
    } else if (data.fakeId) {
      const subDirectionsIndex = this.subDirections.findIndex((e) => e.fakeId === data.fakeId);
      if (subDirectionsIndex > -1) this.subDirections.splice(subDirectionsIndex, 1);
    }

    this._setSubDirections();
  }

  private _setSubDirections(): void {
    this.filteredSubDirections.forEach(
      (e) =>
        (e.auxiliaryDirectionRequests = e.auxiliaryDirectionRequests?.filter(
          (e) => e.status != Constants.OBJECT_STATUS.REMOVE
        ))
    );

    this.filteredSubDirections = AdaaHelper.clone(
      this.subDirections.filter((e) => e.status !== Constants.OBJECT_STATUS.REMOVE)
    );

    this.directionForm.get("subdirections")?.setValue(this.subDirections);
  }

  private _checkForDuplicateName({ nameAE, nameEN, fakeId, id }: SubGovDirection): boolean {
    if (id) {
      return this.subDirections.findIndex((e) => (e.nameAE === nameAE || e.nameEN === nameEN) && e.id !== id) > -1;
    } else if (fakeId) {
      return (
        this.subDirections.findIndex((e) => (e.nameAE === nameAE || e.nameEN === nameEN) && e.fakeId !== fakeId) > -1
      );
    }
    return false;
  }

  private _prepareForm() {
    this.directionForm = this._formBuilder.group({
      id: { value: null, disabled: this.isViewMode() },
      refCode: { value: null, disabled: this.isViewMode() },
      nameAE: { value: null, disabled: this.isViewMode() },
      nameEN: { value: null, disabled: this.isViewMode() },
      dscAE: { value: null, disabled: this.isViewMode() },
      dscEN: { value: null, disabled: this.isViewMode() },
      startDate: { value: AdaaHelper.plan?.startYear as number, disabled: this.isViewMode() },
      endDate: { value: AdaaHelper.plan?.endYear as number, disabled: this.isViewMode() },
      entityId: [{ value: Constants.CONSTANT_PMO_ID, disabled: true }], //By default entityId is PMO this ddl only for display
      owner: { value: 0, disabled: this.isViewMode() },
      strategyOwnerGroup: { value: 0, disabled: this.isViewMode() },
      vision: { value: AdaaBoolean.N, disabled: this.isViewMode() },
      available: { value: AdaaBoolean.N, disabled: this.isViewMode() },
      isNationalStrategy: { value: false, disabled: this.isViewMode() },
      nationalStrategyType: { value: null, disabled: true },
      logo: { value: null, disabled: this.isViewMode() },
      subdirections: { value: null, disabled: this.isViewMode() },
      visionType: { value: null, disabled: this.isViewMode() },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_GOVERNMENT_DIRECTIONS, this.directionForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.directionForm = response.form;
        },
      });
  }
}
